import { FCC } from "@/types/common";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import { useEffect, useState } from "react";
import { getDynamicRoute } from "@/utils/url";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { IconActionDelete, IconEditor } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { TalentDeleteErrorModal } from "@/features/talent/components/talent-common/TalentDeleteErrorModal";
import { useDialog } from "@/hooks/useDialog";
import { deleteTalentApi } from "@/api/services/main/talent";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { queryClient } from "@/constants/queryClient";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { TalentProfileHeader } from "@/features/talent/components/talent-detail/TalentProfileHeader";
import { TalentRecruitmentStatus } from "@/features/talent/components/talent-detail/TalentRecruitmentStatus";
import { TalentBasicInfo } from "@/features/talent/components/talent-detail/TalentBasicInfo";
import { TalentResidenceStatusInfo } from "@/features/talent/components/talent-detail/TalentResidenceStatusInfo";
import { TalentLanguageAbility } from "@/features/talent/components/talent-detail/TalentLanguageAbility";
import { TalentCertificateFiles } from "@/features/talent/components/talent-detail/TalentCertificateFiles";
import { TalentEducationBackground } from "@/features/talent/components/talent-detail/TalentEducationBackground";
import { TalentWorkExperience } from "@/features/talent/components/talent-detail/TalentWorkExperience";
import { TalentPartner } from "@/features/talent/components/talent-detail/TalentPartner";
import { TalentMemo } from "@/features/talent/components/talent-detail/TalentMemo";
import { TalentInterviewEvaluation } from "@/features/talent/components/talent-detail/TalentInterviewEvaluation";
import { TalentFamilyInfo } from "@/features/talent/components/talent-detail/TalentFamilyInfo";
import { TalentDesiredCondition } from "@/features/talent/components/talent-detail/TalentDesiredCondition";
import { TalentInternalMemo } from "@/features/talent/components/talent-detail/TalentInternalMemo";
import { TalentFormComment } from "@/features/talent/components/talent-form/TalentFormComment";
import { TALENT_WIDTH_FULL, TALENT_WIDTH_LEFT_COLUMN, TALENT_WIDTH_RIGHT_COLUMN } from "@/features/talent/constants/width";
import { TalentProjectParticipationStatus } from "@/features/talent/components/talent-detail/TalentProjectParticipationStatus";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { useScrollToElement } from "@/hooks/useScrollToElement";
const TALENT_LIST_URL_STORAGE_KEY = "talent_list_url";

type TTalentDetailProps = {
  defaultValues?: Partial<TTalentForm>;
};

export const TalentDetailContent: FCC<TTalentDetailProps> = ({ defaultValues }) => {
  if (!defaultValues) return;

  const { talentId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!talentId) return navigate(AppRoutes.talentList);
  }, [talentId]);

  useScrollToElement("passport_code_input");

  const editRoute = getDynamicRoute(AppRoutes.talentEdit, { talentId: talentId });

  const confirmDelete = useDialog();
  const errorDelete = useDialog();
  const [confirmMessage, setConfirmMessage] = useState<string[]>([]);

  const getBackUrl = () => {
    return sessionStorage.getItem(TALENT_LIST_URL_STORAGE_KEY) || AppRoutes.talentList;
  };

  const handleBackClick = () => {
    navigate(getBackUrl(), { state: { preserveSelection: true } });
  };

  const handleDelete = async (talentId: number) => {
    try {
      const hasCaseMatches = (defaultValues?.caseMatches?.length ?? 0) > 0;

      if (hasCaseMatches) {
        await errorDelete.show();
        return;
      }

      setConfirmMessage(["人材情報を削除しますがよろしいですか？この操作は元に戻せないため、ご注意ください。"]);
      const confirm = await confirmDelete.show();

      if (!confirm) return;
      const rs = await deleteTalentApi([talentId]);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TALENT.FETCH_LIST] });
      navigate(getBackUrl());
    } catch (error) {
      showError(error);
    }
  };

  return (
    <>
      <Stack mt={1}>
        <TalentProfileHeader defaultValues={defaultValues} detailPage={true} />
        <Box width="100%">
          <Grid container spacing={4} mb={4}>
            {/* Left column */}
            <Grid item xs={12} md={8} mt={1}>
              <TalentRecruitmentStatus defaultValues={defaultValues} />
              <TalentBasicInfo defaultValues={defaultValues} />
              <TalentResidenceStatusInfo defaultValues={defaultValues} />
              <TalentLanguageAbility defaultValues={defaultValues} />
              <TalentEducationBackground defaultValues={defaultValues} />
              <TalentWorkExperience defaultValues={defaultValues} />
              <TalentCertificateFiles defaultValues={defaultValues} />
              <TalentMemo defaultValues={defaultValues} />
              <TalentInterviewEvaluation defaultValues={defaultValues} />
              <TalentFamilyInfo defaultValues={defaultValues} />
            </Grid>
            <Grid item xs={12} md={4} mt={1}>
              {/* Right */}
              <TalentProjectParticipationStatus defaultValues={defaultValues} />
              <TalentDesiredCondition defaultValues={defaultValues} />
              <TalentInternalMemo defaultValues={defaultValues} />
              <TalentFormComment />
              <TalentPartner defaultValues={defaultValues} />
            </Grid>
          </Grid>
          <FooterContainer>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button variant="ghost" size="sm" onClick={handleBackClick} sx={{ width: "60px" }}>
                戻る
              </Button>
              <Stack direction="row" gap={2}>
                <Button
                  variant="danger"
                  size="sm"
                  color="error"
                  onClick={async () => await handleDelete(Number(talentId))}
                  startIcon={<IconActionDelete />}
                >
                  削除
                </Button>
                <Button variant="outline" size="sm" color="error" onClick={() => navigate(editRoute)} startIcon={<IconEditor />}>
                  編集
                </Button>
              </Stack>
            </Stack>
          </FooterContainer>
        </Box>
      </Stack>

      <ConfirmSaveModal open={confirmDelete.open} onOk={confirmDelete.confirm} onClose={confirmDelete.cancel}>
        {confirmMessage.map((message, index) => (
          <Typography component="p" variant="subtitle1" key={index}>
            {message}
          </Typography>
        ))}
      </ConfirmSaveModal>

      <TalentDeleteErrorModal open={errorDelete.open} onClose={errorDelete.cancel} caseMatches={defaultValues?.caseMatches ?? []} />
    </>
  );
};
